import React, { useCallback, useEffect, useState } from "react";
import { Loader2, KeyRound, CheckCircle2, AlertTriangle, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import api, { formatApiError } from "@/lib/api";
import { toast } from "@/components/ui/sonner";

const SEV_STYLES = {
    ok:      { badge: "bg-emerald-500/10 text-emerald-600", label: "Fresh" },
    amber:   { badge: "bg-amber-500/10 text-amber-600",     label: "Due soon" },
    red:     { badge: "bg-red-500/10 text-red-600",         label: "Overdue" },
    unknown: { badge: "bg-muted text-muted-foreground",     label: "Never rotated" },
};

export default function KeyRotationModal({ onClose, onChange }) {
    const [items, setItems] = useState([]);
    const [thresholds, setThresholds] = useState({ amber_days: 90, red_days: 180 });
    const [loading, setLoading] = useState(true);
    const [markingKey, setMarkingKey] = useState(null);

    const load = useCallback(async () => {
        try {
            const { data } = await api.get("/admin/key-rotation");
            setItems(data.items || []);
            setThresholds(data.thresholds || { amber_days: 90, red_days: 180 });
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setLoading(false); }
    }, []);
    useEffect(() => { load(); }, [load]);

    const markRotated = async (item) => {
        if (!window.confirm(`Confirm that you've rotated ${item.label} in the provider console AND updated backend/.env?`)) return;
        setMarkingKey(item.key);
        try {
            await api.post(`/admin/key-rotation/${item.key}/mark-rotated`, {});
            toast.success(`${item.label} marked as rotated.`);
            await load();
            onChange?.();
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setMarkingKey(null); }
    };

    return (
        <div
            className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4"
            onClick={onClose}
            data-testid="key-rotation-modal"
        >
            <div
                className="bg-background border border-border rounded-lg max-w-2xl w-full max-h-[85vh] overflow-hidden flex flex-col"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="p-4 border-b border-border flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <KeyRound size={16} className="text-violet-500" />
                        <div>
                            <div className="font-medium text-sm">Secret rotation tracker</div>
                            <div className="text-[11px] text-muted-foreground">
                                Amber ≥ {thresholds.amber_days}d · Red ≥ {thresholds.red_days}d. Rotate the secret in the provider console + backend/.env, then click <b>Mark rotated</b>.
                            </div>
                        </div>
                    </div>
                    <button onClick={onClose} className="p-1 hover:bg-muted rounded" data-testid="key-rotation-close">
                        <X size={16} />
                    </button>
                </div>

                <div className="flex-1 overflow-auto">
                    {loading ? (
                        <div className="py-10 flex justify-center"><Loader2 size={18} className="animate-spin text-violet-500" /></div>
                    ) : (
                        <div className="divide-y divide-border">
                            {items.map((item) => {
                                const sev = SEV_STYLES[item.severity] || SEV_STYLES.unknown;
                                return (
                                    <div key={item.key} className="p-3 flex items-center gap-3" data-testid={`key-row-${item.key}`}>
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-2">
                                                <div className="font-medium text-sm">{item.label}</div>
                                                <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${sev.badge}`} data-testid={`key-sev-${item.key}`}>
                                                    {sev.label}
                                                </span>
                                            </div>
                                            <div className="text-[11px] text-muted-foreground mt-0.5 font-mono">
                                                {item.env_var}
                                                {item.days_since !== null && item.days_since !== undefined
                                                    ? ` · ${item.days_since} day${item.days_since === 1 ? "" : "s"} ago`
                                                    : " · never rotated"}
                                                {item.rotated_by ? ` · by ${item.rotated_by}` : ""}
                                            </div>
                                        </div>
                                        <Button
                                            size="sm"
                                            variant="outline"
                                            onClick={() => markRotated(item)}
                                            disabled={markingKey === item.key}
                                            data-testid={`key-mark-${item.key}`}
                                        >
                                            {markingKey === item.key
                                                ? <Loader2 size={12} className="animate-spin mr-1.5" />
                                                : <CheckCircle2 size={12} className="mr-1.5" />}
                                            Mark rotated
                                        </Button>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>

                <div className="p-3 border-t border-border text-[11px] text-muted-foreground flex items-center gap-1.5">
                    <AlertTriangle size={11} /> This only tracks WHEN you rotated — the actual rotation must happen in the provider console + backend/.env.
                </div>
            </div>
        </div>
    );
}
